import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
    EnterpriseLegalAgreementsService, GenerateAgreementsFullRequestParams, GenerateAgreementsResponse,
    IsbExperienceService, isbExperienceServiceToken, MoneyProcessingService,
    MoneyProcessingServiceToken, PayorInfo, StoreAgreementAcknowledgmentsRequestParams, StoreAgreementAcknowledgmentsResponse, BankCard
} from '@nationwide/dgs-angular-billing-common';
import { catchError, mergeMap, forkJoin, map, Observable, of, retry, tap, timeout } from 'rxjs';
import { DistributionPartnerRoleInfoAdapter } from '../../../../../../billing/shared/distribution-partner-role-info/distribution-partner-role-info.adapter';
import { DistributionPartnerRoleInfoService } from '../../../../../../billing/shared/distribution-partner-role-info/distribution-partner-role-info.service';
import { LoggerService } from '../../../../../../shared/logger/logger.service';
import { submitPaymetCardTypeMap } from '../../../../../../shared/payments/card-utils/card-type.enum';
import { SessionService } from '../../../../../../shared/session/session.service';
import { EditAutomaticPaymentFlowService } from '../services/edit-automatic-payments-flow-service';
import { EditAutomaticPaymentsReview } from './edit-automatic-payments-review.model';
import { environment } from '../../../../../../../environments/environment';
import { NuxeoService } from '../../../../../../shared/service/nuxeo/nuxeo.service';
import { BankCardPaymentItem } from '../../../../shared/models/bankcard-item.model';
import { dpimLoggerCostant } from '../../../../../../billing/shared/distribution-partner-role-info/logger.constant';

@Injectable()
export class EditAutomaticPaymentsReviewService {
    wasAutoPayAuthorizationFormSuccessfullyGenerated: boolean;

    constructor(
        private editPaymentFlowService: EditAutomaticPaymentFlowService,
        protected sessionService: SessionService,
        @Inject(isbExperienceServiceToken) private isbExperience: IsbExperienceService,
        private review: EditAutomaticPaymentsReview,
        private datePipe: DatePipe,
        @Inject(MoneyProcessingServiceToken) private moneyProcessing: MoneyProcessingService,
        @Inject('logger') private logger: LoggerService,
        private distributionPartnerRoleInfoService: DistributionPartnerRoleInfoService,
        private distributionPartnerRoleInfoAdapter: DistributionPartnerRoleInfoAdapter,
        private enterpriseLegalAgreementsService: EnterpriseLegalAgreementsService,
        private nuxeoService: NuxeoService
    ) {
    }

    getAutomaticPaymentsAuthorizationForm(): Observable<GenerateAgreementsResponse> {
        const params: GenerateAgreementsFullRequestParams = this.nuxeoService.buildGenerateAgreementsFullPayload(this.editPaymentFlowService.selectedPayMethod, false);
        return this.enterpriseLegalAgreementsService.generateAgreements(params).pipe(
            timeout(environment.TIMEOUT.apiCallDuration.enterpriseLegalAgreements),
            retry(1),
            catchError((error) => {
                this.logger.error('Edit Automatic Payments Review - generate authorization agreement failed', { error });
                throw of(error);
            })
        );
    }

    storeAgreementAcknowledgementsForRecurring(): Observable<StoreAgreementAcknowledgmentsResponse> {
        const params: StoreAgreementAcknowledgmentsRequestParams = this.nuxeoService.buildStoreAgreementAcknowledgementsPayload(this.editPaymentFlowService.selectedPayMethod, false);
        return this.enterpriseLegalAgreementsService.storeAgreementAcknowledgments(params).pipe(
            tap(() => this.editPaymentFlowService.wasRecurringAuthorizationFormSuccessfullySaved = true),
            timeout(environment.TIMEOUT.apiCallDuration.enterpriseLegalAgreements),
            retry(1),
            catchError((error) => {
                this.logger.error('Edit Automatic Payments Review - save authorization agreement failed', { error });
                this.editPaymentFlowService.wasRecurringAuthorizationFormSuccessfullySaved = false;
                throw of(error);
            })
        );
    }

    submit(): Observable<EditAutomaticPaymentResponse> {
        return this.requestPlaceHolder().pipe(
            mergeMap(() =>
                forkJoin({
                    updateAutomaticPaymentMethod: this.updateAutomaticPaymentMethod(),
                    distributionPartnerRoleInfoResponse: this.distributionPartnerRoleInfoService.fetchDistributionPartnerRoleInfo()
                })
            ),
            tap((responses) => {
                this.distributionPartnerRoleInfoAdapter.mapDistributionPartnerRoleResponse(responses.distributionPartnerRoleInfoResponse, dpimLoggerCostant.EDIT_AUTOMATIC_PAYMENT);
            }),
            mergeMap((previousCalls) =>
                forkJoin({
                    updateAutomaticPaymentMethod: of(previousCalls.updateAutomaticPaymentMethod),
                    nbpEmailSentSuccessfully: this.sessionService.billingSystem === 'NBP' ? this.sendNbpEmail() : this.requestPlaceHolder()
                })
            )
        );
    }

    sendNbpEmail(): Observable<boolean> {
        let response: Observable<boolean>;

        if (this.editPaymentFlowService.successfullyUpdatedEnrollment) {
            response = this.isbExperience.sendEditAutomaticPaymentsEmail({
                accessToken: this.sessionService.accessToken,
                billingSystem: this.sessionService.billingSystem,
                omsData: {
                    omsServiceTransformationStrategyData: {
                        servicePlusIndicator: this.sessionService.dpimServicePlusIndicator,
                        agentPhoneNumber: this.sessionService.dpimAgentPhoneNumber
                    }
                }
            }).pipe(
                map(() => true),
                catchError(() => of(false))
            );
        } else {
            response = this.requestPlaceHolder();
        }

        return response;
    }

    deleteCamCache(): void {
        this.isbExperience.deleteCamCache({
            ecn: this.sessionService.ecn,
            accessToken: this.sessionService.accessToken
        }).subscribe({
            next: (response) => {
                this.logger.info('CAM Cache deleted successfully in Edit Auto Pay flow', response);
            },
            error: (error) => {
                this.logger.error('CAM Cache deletion failed in Edit Auto Pay flow', {
                    error: error.error || error
                });
            }
        });
    }

    private updateAutomaticPaymentMethod(): Observable<any> {
        return this.billingService.patchPayPlan({
            agreementNumber: this.sessionService.billingAccountNumber,
            accessToken: this.sessionService.accessToken,
            billingSystem: this.sessionService.billingSystem,
            payload: {
                billingPayload: <any>{
                    enterpriseCustomerNumber: this.sessionService.ecn,
                    requestType: 'BillingMethodChange.PayPlanUpdate',
                    action: 'update',
                    currentBillingMethodType: this.editPaymentFlowService.currentBillingMethodType,
                    newBillingMethodType: this.review.isBankCardFlow ? 'Recurring Bankcard' : 'Recurring EFT',
                    ...this.sessionService.billingSystem === 'SAP Billing' && {
                        paymentMethodId: this.review.paymentMethodId || undefined
                    },
                    accountDueDate: this.datePipe.transform(this.editPaymentFlowService.nextBillDueDate, 'MM-dd-yyyy', 'UTC'),
                    paymentMethod: {
                        payorInfo: this.sessionService.billingSystem === 'SAP Billing' || this.review.isBankAccountFlow ? undefined : this.payorInfo,
                        paymentMethodType: this.review.isBankCardFlow ?
                            'BankCard.PaymentMethod' : 'ElectronicFundsTransfer.PaymentMethod',
                        description: 'UNSET',
                        electronicFundsTransfer: this.review.isBankAccountFlow ? {
                            bankName: this.review.bankName,
                            bankRoutingNumber: this.review.routingNumber,
                            bankAccountNumber: this.review.bankAccountNumber,
                            bankAccountType: this.review.accountType,
                            encryptedBankAccountNumber: this.review.bankAccountNumber,
                            maskedBankAccountNumber: this.review.accountNumberLastThree
                        } : undefined,
                        bankCard: this.review.isBankCardFlow ? <any> this.getBankCardDetails() : undefined
                    }
                }
            }
        }).pipe(
            tap(() => this.editPaymentFlowService.save({ successfullyUpdatedEnrollment: true })),
            catchError((error) => {
                this.logger.error('API ERROR: EDIT AUTOMATIC PAYMENTS SUBMISSION', error);
                this.editPaymentFlowService.save({
                    successfullyUpdatedEnrollment: false, errorDetails: {
                        errorMessage: error.error?.developerMessage,
                        errorCode: error.status
                    }
                });
                return of(error);
            })
        );
    }
    private getBankCardDetails(): BankCard {
        const bankCard: BankCard = {
            encryptedPan: undefined,
            encryptedPanKey: undefined,
            encryptedPanPubKeyFingerPrint: undefined,
            expirationDate: this.fetchExpirationDate(this.review.cardExpirationYear, this.review.cardExpirationMonth),
            ccLastFour: (<BankCardPaymentItem> this.editPaymentFlowService.selectedPayMethod.item).cardNumberLastFour,
            cardBrand: <'MasterCard' | 'American Express' | 'Visa' | 'Discover'>submitPaymetCardTypeMap.get(this.review.cardType)
        };
        if (this.sessionService.billingSystem === 'NBP') {
            bankCard.profileId = (<BankCardPaymentItem> this.editPaymentFlowService.selectedPayMethod.item).profileId;
            bankCard.encryptedPan = '';
        }
        return bankCard;
    }

    private requestPlaceHolder(): Observable<null> {
        return of(null);
    }

    get payorInfo(): PayorInfo {
        return {
            firstName: this.review.firstName,
            middleName: this.review.middleInitial,
            lastName: this.review.lastName,
            fullName: this.review.payorName,
            addressLine1: this.review.street,
            addressLine2: this.review.apartmentNumber,
            city: this.review.city,
            state: this.review.stateName,
            postalCode: this.review.zip
        };
    }

    private get billingService(): IsbExperienceService | MoneyProcessingService {
        const service: IsbExperienceService | MoneyProcessingService = this.sessionService.billingSystem === 'NBP' ? this.moneyProcessing : this.isbExperience;
        return service;
    }

    private fetchExpirationDate(cardExpirationYear: string, cardExpirationMonth: string): string {
        return this.sessionService.isSAP ?
            `${cardExpirationYear}${cardExpirationMonth}` : `${cardExpirationYear}-${cardExpirationMonth}`;
    }
}

export interface EditAutomaticPaymentResponse {
    updateAutomaticPaymentMethod?: boolean | HttpErrorResponse | null;
    updateEmailAddress?: boolean | null;
    nbpEmailSentSuccessfully?: boolean | null;
}
